import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { Menu } from '@actual-app/components/menu';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { AiCategorizeSuggestion } from '@actual-app/core/server/ai/app';
import type { TFunction } from 'i18next';

import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
} from '#components/common/Modal';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { AiCategorizeSuggestionRow } from './AiCategorizeSuggestionRow';
import type { SuggestionGroup } from './AiCategorizeSuggestionRow';

type AiCategorizeModalProps = Extract<
  ModalType,
  { name: 'ai-categorize' }
>['options'];

export function AiCategorizeModal({ accountId }: AiCategorizeModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useFormat();
  const { isNarrowWidth } = useResponsive();
  const { data: categories } = useCategories();
  const [groups, setGroups] = useState<SuggestionGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [result, rules] = await Promise.all([
        send('ai-categorize', { accountId }),
        send('rules-get'),
      ]);
      if (cancelled) return;
      if ('error' in result) {
        setError(result.error);
        return;
      }
      const payeesWithRules = new Set(
        rules.flatMap(rule =>
          rule.conditions
            .filter(c => c.field === 'payee' && c.op === 'is')
            .map(c => String(c.value)),
        ),
      );
      setGroups(groupSuggestions(result.suggestions, payeesWithRules));
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const updateGroup = (key: string, fields: Partial<SuggestionGroup>) =>
    setGroups(current =>
      current
        ? current.map(g => (g.key === key ? { ...g, ...fields } : g))
        : current,
    );

  const selected = (groups ?? []).filter(g => g.include && g.categoryId);
  const selectedCount = selected.reduce((n, g) => n + g.transactions.length, 0);

  const onApply = async (close: () => void) => {
    setIsApplying(true);
    await send('transactions-batch-update', {
      updated: selected.flatMap(({ categoryId, transactions }) =>
        categoryId
          ? transactions.map(tx => ({
              id: tx.transactionId,
              category: categoryId,
            }))
          : [],
      ),
      learnCategories: false,
    });

    const ruleGroups = new Map(
      selected
        .filter(g => g.createRule && g.canCreateRule && g.payeeId)
        .map(g => [g.payeeId, g]),
    );
    for (const g of ruleGroups.values()) {
      await send('rule-add', {
        stage: null,
        conditionsOp: 'and',
        conditions: [
          { op: 'is', field: 'payee', value: g.payeeId ?? '', type: 'id' },
        ],
        actions: [
          { op: 'set', field: 'category', value: g.categoryId, type: 'id' },
        ],
      });
    }

    dispatch(
      addNotification({
        notification: {
          type: 'message',
          message: t(
            'Categorized {{count}} transactions and created {{rules}} rules.',
            { count: selectedCount, rules: ruleGroups.size },
          ),
        },
      }),
    );
    close();
  };

  const categoryOptions = (categories?.grouped ?? [])
    .filter(group => !group.hidden)
    .flatMap(group => [
      [Menu.label, group.name] as const,
      ...(group.categories ?? [])
        .filter(c => !c.hidden)
        .map(c => [c.id, c.name] as const),
    ]);

  return (
    <Modal
      name="ai-categorize"
      containerProps={{
        style: isNarrowWidth
          ? { width: '100vw', maxWidth: '100vw', height: '100vh', margin: 0 }
          : { width: 900 },
      }}
    >
      {({ state }) => (
        <View
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <ModalHeader
            title={t('Categorize with Claude')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ padding: '0 20px', flex: 1, minHeight: 0 }}>
            {error ? (
              <Text style={{ color: theme.errorText }}>
                {describeError(error, t)}
              </Text>
            ) : !groups ? (
              <View style={{ alignItems: 'center', padding: 40, gap: 10 }}>
                <AnimatedLoading width={25} height={25} />
                <Text style={{ color: theme.pageTextLight }}>
                  <Trans>
                    Claude is reviewing your uncategorized transactions…
                  </Trans>
                </Text>
              </View>
            ) : groups.length === 0 ? (
              <Text>
                <Trans>There are no uncategorized transactions here.</Trans>
              </Text>
            ) : (
              <>
                <Text style={{ marginBottom: 10, color: theme.pageTextLight }}>
                  <Trans>
                    Review the suggestions below. Nothing changes until you
                    apply them. Rules categorize future transactions from the
                    same payee automatically.
                  </Trans>
                </Text>
                <View style={{ overflow: 'auto', flex: 1, maxHeight: '60vh' }}>
                  {groups.map(group => (
                    <AiCategorizeSuggestionRow
                      key={group.key}
                      group={group}
                      categoryOptions={categoryOptions}
                      formatAmount={amount => format(amount, 'financial')}
                      onChange={fields => updateGroup(group.key, fields)}
                    />
                  ))}
                </View>
              </>
            )}
          </View>
          <ModalButtons style={{ padding: 20 }}>
            <Button style={{ marginRight: 10 }} onPress={() => state.close()}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="primary"
              isDisabled={selectedCount === 0 || isApplying}
              onPress={() => void onApply(() => state.close())}
            >
              <Trans count={selectedCount}>
                Apply to {{ count: selectedCount }} transactions
              </Trans>
            </Button>
          </ModalButtons>
        </View>
      )}
    </Modal>
  );
}

// One row per payee + suggested category, biggest groups first. A rule is only
// offered when every suggestion for the payee agrees and it has no rule yet.
function groupSuggestions(
  suggestions: AiCategorizeSuggestion[],
  payeesWithRules: Set<string>,
): SuggestionGroup[] {
  const categoriesByPayee = new Map<string, Set<string | null>>();
  for (const s of suggestions) {
    if (!s.payeeId) continue;
    const set = categoriesByPayee.get(s.payeeId) ?? new Set();
    set.add(s.categoryId);
    categoriesByPayee.set(s.payeeId, set);
  }

  const groups = new Map<string, SuggestionGroup>();
  for (const s of suggestions) {
    const payeeKey = s.payeeId ?? s.importedPayee ?? '';
    const key = `${payeeKey}|${s.categoryId ?? ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.transactions.push(s);
      continue;
    }
    const canCreateRule =
      s.payeeId != null &&
      s.categoryId != null &&
      categoriesByPayee.get(s.payeeId)?.size === 1 &&
      !payeesWithRules.has(s.payeeId);
    groups.set(key, {
      key,
      payeeId: s.payeeId,
      label: s.payeeName ?? s.importedPayee ?? '—',
      categoryId: s.categoryId,
      reason: s.reason,
      transactions: [s],
      include: s.categoryId != null,
      createRule: canCreateRule,
      canCreateRule,
    });
  }

  return [...groups.values()].sort(
    (a, b) => b.transactions.length - a.transactions.length,
  );
}

function describeError(code: string, t: TFunction) {
  switch (code) {
    case 'ai-invalid-key':
      return t(
        'The Anthropic API key on your server was rejected. Check the key configured there.',
      );
    case 'ai-rate-limited':
      return t('Claude is rate limited right now. Try again in a minute.');
    case 'ai-unreachable':
    case 'network-failure':
      return t('Could not reach Claude. Check your connection and try again.');
    case 'ai-refused':
      return t('Claude declined to categorize these transactions.');
    case 'ai-output-truncated':
      return t(
        'There were too many transactions to categorize at once. Try one account at a time.',
      );
    default:
      return t('Could not get suggestions ({{code}}). Try again later.', {
        code,
      });
  }
}
